// Country dialling codes for the WhatsApp input.
//
// STORAGE DOES NOT CHANGE. `coaches.whatsapp` stays a single free-text column
// holding digits (today: `14378791830`). The form splits it into two controls
// for entry and recomposes on save, so `waHref` in
// src/components/profile/CoachContact.tsx — which does
// `whatsapp.replace(/\D/g, '')` — and every other consumer keep working with
// no migration.

export interface DialCode {
  /** Digits only, no '+'. This is what gets concatenated into storage. */
  code: string
  country: string
}

// Ordered for the <select>: SEA first (beta market), then the rest by country.
// Codes are not unique — +1 covers USA and Canada, +7 Russia and Kazakhstan —
// so the select is keyed by country, and splitting a stored number can only
// ever recover the CODE, never which country it belonged to. That is fine:
// the code is all that is stored.
export const DIAL_CODES: readonly DialCode[] = [
  { code: '84', country: 'Vietnam' },
  { code: '66', country: 'Thailand' },
  { code: '855', country: 'Cambodia' },
  { code: '856', country: 'Laos' },
  { code: '95', country: 'Myanmar' },
  { code: '63', country: 'Philippines' },
  { code: '62', country: 'Indonesia' },
  { code: '60', country: 'Malaysia' },
  { code: '65', country: 'Singapore' },
  { code: '673', country: 'Brunei' },
  { code: '1', country: 'USA / Canada' },
  { code: '20', country: 'Egypt' },
  { code: '27', country: 'South Africa' },
  { code: '30', country: 'Greece' },
  { code: '31', country: 'Netherlands' },
  { code: '32', country: 'Belgium' },
  { code: '33', country: 'France' },
  { code: '34', country: 'Spain' },
  { code: '351', country: 'Portugal' },
  { code: '352', country: 'Luxembourg' },
  { code: '353', country: 'Ireland' },
  { code: '354', country: 'Iceland' },
  { code: '355', country: 'Albania' },
  { code: '356', country: 'Malta' },
  { code: '357', country: 'Cyprus' },
  { code: '358', country: 'Finland' },
  { code: '359', country: 'Bulgaria' },
  { code: '36', country: 'Hungary' },
  { code: '370', country: 'Lithuania' },
  { code: '371', country: 'Latvia' },
  { code: '372', country: 'Estonia' },
  { code: '373', country: 'Moldova' },
  { code: '374', country: 'Armenia' },
  { code: '375', country: 'Belarus' },
  { code: '380', country: 'Ukraine' },
  { code: '381', country: 'Serbia' },
  { code: '382', country: 'Montenegro' },
  { code: '383', country: 'Kosovo' },
  { code: '385', country: 'Croatia' },
  { code: '386', country: 'Slovenia' },
  { code: '387', country: 'Bosnia and Herzegovina' },
  { code: '389', country: 'North Macedonia' },
  { code: '39', country: 'Italy' },
  { code: '40', country: 'Romania' },
  { code: '41', country: 'Switzerland' },
  { code: '420', country: 'Czech Republic' },
  { code: '421', country: 'Slovakia' },
  { code: '43', country: 'Austria' },
  { code: '44', country: 'United Kingdom' },
  { code: '45', country: 'Denmark' },
  { code: '46', country: 'Sweden' },
  { code: '47', country: 'Norway' },
  { code: '48', country: 'Poland' },
  { code: '49', country: 'Germany' },
  { code: '51', country: 'Peru' },
  { code: '52', country: 'Mexico' },
  { code: '54', country: 'Argentina' },
  { code: '55', country: 'Brazil' },
  { code: '56', country: 'Chile' },
  { code: '57', country: 'Colombia' },
  { code: '58', country: 'Venezuela' },
  { code: '7', country: 'Russia / Kazakhstan' },
  { code: '81', country: 'Japan' },
  { code: '82', country: 'South Korea' },
  { code: '86', country: 'China' },
  { code: '852', country: 'Hong Kong' },
  { code: '853', country: 'Macau' },
  { code: '886', country: 'Taiwan' },
  { code: '90', country: 'Turkey' },
  { code: '91', country: 'India' },
  { code: '92', country: 'Pakistan' },
  { code: '93', country: 'Afghanistan' },
  { code: '94', country: 'Sri Lanka' },
  { code: '961', country: 'Lebanon' },
  { code: '962', country: 'Jordan' },
  { code: '966', country: 'Saudi Arabia' },
  { code: '971', country: 'United Arab Emirates' },
  { code: '972', country: 'Israel' },
  { code: '974', country: 'Qatar' },
  { code: '976', country: 'Mongolia' },
  { code: '98', country: 'Iran' },
  { code: '234', country: 'Nigeria' },
  { code: '254', country: 'Kenya' },
  { code: '221', country: 'Senegal' },
  { code: '237', country: 'Cameroon' },
  { code: '244', country: 'Angola' },
  { code: '61', country: 'Australia' },
  { code: '64', country: 'New Zealand' },
]

// Longest code first, so '855' (Cambodia) is tested before '85' and '8'.
const CODES_BY_LENGTH = [...new Set(DIAL_CODES.map((d) => d.code))].sort(
  (a, b) => b.length - a.length,
)

/**
 * Best-effort split of a stored WhatsApp value into dialling code + national
 * number.
 *
 * Returns an EMPTY code when no confident split exists, putting the whole
 * value in `number`. That is deliberate: a wrong country code is worse than an
 * unsplit number, because it silently produces a wa.me link to someone else.
 * A national number stored without a country code (e.g. a leading 0) has no
 * recoverable country, so it stays whole.
 */
export function splitWhatsapp(stored: string | null | undefined): {
  code: string
  number: string
} {
  const raw = (stored ?? '').trim()
  if (!raw) return { code: '', number: '' }

  // ONLY a '+'-prefixed value carries an unambiguous country code (C1's
  // storage format). A legacy value without one is returned WHOLE, with the
  // code left unselected — C2b.
  //
  // This guard is load-bearing, not defensive tidying. Prefix-matching a bare
  // number guesses, and it guesses WRONG on real data: Kaillum's stored
  // '4379613497' matches '43' and would come back as Austria +43 / 79613497.
  // He would then save a Canadian number as '+4379613497' without ever seeing
  // that a country had been chosen for him. Same class of mistake as
  // defaulting his education to Bachelor's.
  if (!raw.startsWith('+')) {
    return { code: '', number: raw.replace(/\D/g, '') }
  }

  const digits = raw.slice(1).replace(/\D/g, '')
  if (!digits) return { code: '', number: '' }
  for (const code of CODES_BY_LENGTH) {
    if (!digits.startsWith(code)) continue
    const rest = digits.slice(code.length)
    // Require a plausible national number behind the code. Without this,
    // a short value like '8412' would split as +84 / '12'.
    if (rest.length >= 6) return { code, number: rest }
  }
  // '+'-prefixed but no code we know. Keep the digits whole rather than
  // silently dropping the '+' meaning.
  return { code: '', number: digits }
}

/**
 * True when the coach has typed a number but not chosen a country code. That
 * combination cannot build a working wa.me link, so it is rejected — C1b,
 * enforced server-side too, this is only the client-side early warning.
 */
export function whatsappNeedsCountryCode(code: string, number: string): boolean {
  return number.replace(/\D/g, '').length > 0 && code.replace(/\D/g, '').length === 0
}

/**
 * Recompose into the single stored column as `+<code><national>`, digits only
 * after the '+' (C1a/C1d — spaces, dashes and brackets are stripped here).
 *
 * Returns '' — which the server maps to NULL — when there is no national
 * number, so a dangling country code is never stored (C1c).
 *
 * When a number is present but no code is selected, this returns the bare
 * digits WITHOUT a '+'. That value is deliberately invalid: the server rejects
 * anything not '+'-prefixed, so a client that skips the local check still
 * cannot store an unreachable number.
 */
export function composeWhatsapp(code: string, number: string): string {
  const n = number.replace(/\D/g, '')
  if (!n) return ''
  const c = code.replace(/\D/g, '')
  if (c) return `+${c}${n}`
  return n
}
